import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Breadcrumb from "../components/BreadCrumb";
import { motion } from "framer-motion";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import "../assets/css/swiper.css";
import { Autoplay, Navigation } from "swiper/modules";
import { fetchGitHubCategories, fetchCategoryMedia } from "../utils/githubMediaUtils";
import { FaSpinner } from "react-icons/fa";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import Fullscreen from "yet-another-react-lightbox/plugins/fullscreen";
import Slideshow from "yet-another-react-lightbox/plugins/slideshow";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import Video from "yet-another-react-lightbox/plugins/video";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import "yet-another-react-lightbox/plugins/thumbnails.css";
import { fetchYouTubePlaylists, fetchPlaylistVideos, getYouTubeEmbedUrl, MOCK_YOUTUBE_DATA } from "../utils/youtubeUtils";

const Portfolio = () => {
  const [isLargeScreen, setIsLargeScreen] = useState(window.innerWidth >= 1024);
  const [categories, setCategories] = useState([]);
  const [categoryMedia, setCategoryMedia] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // YouTube data state
  const [youtubePlaylists, setYoutubePlaylists] = useState([]);
  const [youtubeVideos, setYoutubeVideos] = useState({});
  const [youtubeLoading, setYoutubeLoading] = useState(true);
  const [youtubeError, setYoutubeError] = useState(null);
  
  // Lightbox state
  const [isOpen, setIsOpen] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [currentCategory, setCurrentCategory] = useState(null);
  
  // YouTube video modal state
  const [currentVideo, setCurrentVideo] = useState(null);
  const videoModalRef = useRef(null);

  const { t } = useTranslation();

  // Fetch categories and media from GitHub
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch all categories (folders)
        const fetchedCategories = await fetchGitHubCategories();
        setCategories(fetchedCategories);
        
        // For each category, fetch its media
        const mediaByCategory = {};
        for (const category of fetchedCategories) {
          const media = await fetchCategoryMedia(category.id);
          mediaByCategory[category.id] = media;
        }
        
        setCategoryMedia(mediaByCategory);
        setError(null);
      } catch (err) {
        console.error("Error fetching portfolio data:", err);
        setError("Failed to load portfolio data. Please try again later.");
        
        // Fallback to using static data if GitHub API fails
        fallbackToStaticData();
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);
  
  // Fetch YouTube data
  useEffect(() => {
    const fetchYoutubeData = async () => {
      setYoutubeLoading(true);
      try {
        // Fetch all playlists
        const fetchedPlaylists = await fetchYouTubePlaylists();
        setYoutubePlaylists(fetchedPlaylists);
        
        // For each playlist, fetch its videos
        const videosByPlaylist = {};
        for (const playlist of fetchedPlaylists) {
          const videos = await fetchPlaylistVideos(playlist.id);
          videosByPlaylist[playlist.id] = videos;
        }
        
        setYoutubeVideos(videosByPlaylist);
        setYoutubeError(null);
      } catch (err) {
        console.error("Error fetching YouTube data:", err);
        setYoutubeError("Failed to load YouTube data. Using fallback content.");
        
        // Fallback to using mock data if YouTube API fails
        setYoutubePlaylists(MOCK_YOUTUBE_DATA.playlists);
        setYoutubeVideos(MOCK_YOUTUBE_DATA.videos);
      } finally {
        setYoutubeLoading(false);
      }
    };
    
    fetchYoutubeData();
  }, []);

  // Handle escape key for dialog
  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === 'Escape' && videoModalRef.current) {
        closeVideoModal();
      }
    };
    
    window.addEventListener('keydown', handleEsc);
    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, []);

  // Fallback function to use static data if GitHub API fails
  const fallbackToStaticData = () => {
    import("../constants/data").then(({ WorksData }) => {
      // Filter out "photo", "works", and "clients" folders
      const filteredData = WorksData.filter(item => 
        item.title.toLowerCase() !== 'photo' && 
        item.title.toLowerCase() !== 'works' &&
        item.title.toLowerCase() !== 'clients'
      );
      
      setCategories(filteredData.map(item => ({
        id: item.title,
        title: t(item.title),
        path: item.title
      })));
      
      const mediaByCategory = {};
      filteredData.forEach(item => {
        mediaByCategory[item.title] = item.features.map(feature => ({
          id: feature.title,
          title: feature.title,
          bannerImage: feature.bannerImage,
          mediaType: feature.videoId ? 'video' : 'image'
        }));
      });
      
      setCategoryMedia(mediaByCategory);
    });
  };

  useEffect(() => {
    window.scrollTo(0, 0);

    const handleResize = () => {
      setIsLargeScreen(window.innerWidth >= 1024);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const paths = [
    { label: t("homepage"), link: "/" },
    { label: t("portfolio"), link: "/portfolio" },
  ];

  const openLightbox = (categoryId, index) => {
    setCurrentCategory(categoryId);
    setPhotoIndex(index);
    setIsOpen(true);
  };
  
  const openYoutubeLightbox = (video) => {
    setCurrentVideo(video);
    if (videoModalRef.current) {
      videoModalRef.current.showModal();
    }
  };
  
  const closeVideoModal = () => {
    if (videoModalRef.current) {
      videoModalRef.current.close();
      // Clear the iframe src to stop the video
      const iframe = videoModalRef.current.querySelector('iframe');
      if (iframe) {
        const src = iframe.src;
        iframe.src = '';
        setTimeout(() => { iframe.src = src; }, 100);
      }
    }
  };

  if (loading) {
    return (
      <div className="w-full bg-white min-h-screen flex items-center justify-center pt-32">
        <FaSpinner className="animate-spin text-primary text-4xl" />
      </div>
    );
  }

  // Prepare slides for lightbox
  const getLightboxSlides = (categoryId) => {
    if (!categoryId || !categoryMedia[categoryId]) return [];
    
    return categoryMedia[categoryId].map(item => {
      if (item.mediaType === 'video') {
        return {
          type: 'video',
          width: 1280,
          height: 720,
          sources: [
            {
              src: item.bannerImage,
              type: `video/${item.bannerImage.split('.').pop().toLowerCase()}`
            }
          ]
        };
      }
      return { src: item.bannerImage };
    });
  };

  return (
    <div className="w-full bg-white min-h-screen">
      <div className="pt-32">
        <Breadcrumb paths={paths} />
      </div>
      <div className="container mx-auto px-3 lg:px-0 pb-32">
        <div className="pt-10 pb-20">
          <div className="flex flex-col leading-relaxed tracking-normal">
            <h1 className="font-northuraBlack text-4xl md:text-6xl lg:text-7xl">{t("our_creative")}</h1>
            <p className="text-3xl md:text-5xl lg:text-6xl font-northuraBold">{t("showcase")}</p>
          </div>
        </div>

        <div className="flex flex-col gap-16 md:gap-24 lg:gap-32">
          {/* YouTube Playlists Section */}
          <div className="relative flex flex-col gap-6 md:gap-8 lg:gap-10">
            <h1 className="text-2xl md:text-3xl lg:text-4xl">{t("aruba_creative")}</h1>
            
            {youtubeLoading ? (
              <div className="flex items-center justify-center py-12">
                <FaSpinner className="animate-spin text-primary text-2xl" />
              </div>
            ) : youtubePlaylists.length > 0 ? (
              youtubePlaylists.map((playlist, playlistIdx) => {
                const videos = youtubeVideos[playlist.id] || [];
                
                // Skip playlists with no videos
                if (videos.length === 0) return null;
                
                // Display only first 6 videos in the swiper
                const displayVideos = videos.slice(0, 6);
                
                return (
                  <div key={playlistIdx} className="flex flex-col gap-4">
                    <div className="flex justify-between items-center">
                      <h2 className="text-xl md:text-2xl">{playlist.title}</h2>
                      <Link 
                        to={`/youtube-gallery/${playlist.id}`}
                        className="text-primary hover:underline flex items-center"
                      >
                        {t("see_all")} 
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-1" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                        </svg>
                      </Link>
                    </div>
                    
                    <div className="flex">
                      <Swiper
                        slidesPerView={1}
                        spaceBetween={20}
                        breakpoints={{
                          640: {
                            slidesPerView: 2,
                          },
                          1024: {
                            slidesPerView: displayVideos.length < 3 ? displayVideos.length : 3,
                          },
                        }}
                        loop={displayVideos.length > 1}
                        modules={[Autoplay, Navigation]}
                        autoplay={{
                          delay: 1500,
                          disableOnInteraction: false,
                          pauseOnMouseEnter: true,
                        }}
                        navigation={displayVideos.length > 1}
                        className={`swiper-container ${displayVideos.length < 3 ? "justify-center" : ""}`}
                      >
                        {displayVideos.map((video, videoIdx) => (
                          <SwiperSlide key={videoIdx} className="cursor-pointer lg:rounded-lg w-full">
                            <div 
                              onClick={() => openYoutubeLightbox(video)} 
                              className="w-full h-full flex flex-col justify-between"
                            >
                              <div className="w-full h-full relative bg-black rounded-lg overflow-hidden group">
                                <img
                                  src={video.thumbnailUrl}
                                  alt={video.title}
                                  className="w-full h-full object-cover rounded-lg transition-opacity duration-500 ease-in-out opacity-80 group-hover:opacity-100"
                                  onError={(e) => {
                                    e.target.src = "/images/placeholder.jpg";
                                  }}
                                  loading="lazy"
                                />
                                <div className="absolute inset-0 flex items-center justify-center">
                                  <div className="w-16 h-16 bg-primary/80 rounded-full flex items-center justify-center">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                  </div>
                                </div>
                              </div>
                              <div className="mt-2">
                                <h3 className="text-lg font-semibold line-clamp-1">{video.title}</h3>
                              </div>
                            </div>
                          </SwiperSlide>
                        ))}
                      </Swiper>
                    </div>
                  </div>
                );
              })
            ) : (
              youtubeError ? (
                <div className="py-6 text-center">
                  <p className="text-red-500">{youtubeError}</p>
                </div>
              ) : (
                <div className="py-6 text-center">
                  <p className="text-gray-500">No YouTube playlists found.</p>
                </div>
              )
            )}
          </div>

          {/* Regular Portfolio Categories */}
          {categories.map((category, idx) => {
            const features = categoryMedia[category.id] || [];
            
            // Skip categories with no media
            if (features.length === 0) return null;
            
            return (
              <div key={idx} className="relative flex flex-col gap-6 md:gap-8 lg:gap-10">
                <div className="flex justify-between items-center">
                  <h1 className="text-2xl md:text-3xl lg:text-4xl">{category.title}</h1>
                  {features.length > 0 && (
                    <Link 
                      to={`/category-gallery/${category.id}`}
                      className="text-primary hover:underline flex items-center"
                    >
                      {t("see_all")} 
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-1" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                      </svg>
                    </Link>
                  )}
                </div>

                <div className="flex">
                  <Swiper
                    slidesPerView={1}
                    spaceBetween={20}
                    breakpoints={{
                      640: {
                        slidesPerView: 2,
                      },
                      1024: {
                        slidesPerView: features.length < 3 ? features.length : 3,
                      },
                    }}
                    loop={features.length > 1}
                    modules={[Autoplay, Navigation]}
                    autoplay={{
                      delay: 1500,
                      disableOnInteraction: false,
                      pauseOnMouseEnter: true,
                    }}
                    navigation={features.length > 1}
                    className={`swiper-container ${features.length < 3 ? "justify-center" : ""}`}
                  >
                    {features.map((feature, featureId) => (
                      <SwiperSlide key={featureId} className="cursor-pointer lg:rounded-lg w-full">
                        <div 
                          onClick={() => openLightbox(category.id, featureId)} 
                          className="w-full h-full flex flex-col justify-between"
                        >
                          {feature.bannerImage && (
                            <div className="w-full h-full relative bg-black rounded-lg overflow-hidden group">
                              <img
                                src={feature.bannerImage}
                                alt={feature.title}
                                className="w-full h-full object-cover rounded-lg transition-opacity duration-500 ease-in-out opacity-80 group-hover:opacity-100"
                                onError={(e) => {
                                  // If rawUrl is available, try it as fallback
                                  if (feature.rawUrl) {
                                    e.target.src = feature.rawUrl;
                                  } else {
                                    e.target.src = "/images/placeholder.jpg";
                                  }
                                }}
                                loading="lazy"
                              />
                              {feature.mediaType === 'video' && (
                                <div className="absolute inset-0 flex items-center justify-center">
                                  <div className="w-16 h-16 bg-primary/80 rounded-full flex items-center justify-center">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </SwiperSlide>
                    ))}
                  </Swiper>
                </div>
              </div>
            );
          })}

          {categories.length === 0 && !loading && !error && (
            <div className="py-20 text-center">
              <p className="text-gray-500">No portfolio categories found.</p>
            </div>
          )}

          {error && (
            <div className="py-10 text-center">
              <p className="text-red-500">{error}</p>
              <p className="mt-2 text-gray-500">Showing fallback content.</p>
            </div>
          )}
        </div>
      </div>

      {/* Lightbox for viewing images */}
      <Lightbox
        open={isOpen}
        close={() => setIsOpen(false)}
        index={photoIndex}
        slides={getLightboxSlides(currentCategory)}
        plugins={[Fullscreen, Slideshow, Thumbnails, Video, Zoom]}
      />
      
      {/* Native HTML dialog for YouTube videos */}
      <dialog
        ref={videoModalRef}
        onClick={(e) => {
          // Close if clicking outside the iframe
          if (e.target === videoModalRef.current) {
            closeVideoModal();
          }
        }}
        style={{
          padding: 0,
          border: 'none',
          backgroundColor: 'transparent',
          maxWidth: '95vw',
          maxHeight: '90vh',
          width: '800px',
          overflow: 'visible',
          borderRadius: '8px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.9)'
        }}
      >
        {currentVideo && (
          <>
            <div style={{ position: 'relative', width: '100%', paddingBottom: '56.25%' }}>
              <iframe
                src={`https://www.youtube.com/embed/${currentVideo.id}?autoplay=1&mute=1&rel=0&modestbranding=1`}
                title={currentVideo.title}
                style={{ 
                  position: 'absolute', 
                  top: 0, 
                  left: 0, 
                  width: '100%', 
                  height: '100%',
                  border: 'none',
                  borderRadius: '8px'
                }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
            <button
              onClick={closeVideoModal}
              style={{
                position: 'absolute',
                top: '-40px',
                right: '-10px',
                backgroundColor: 'white',
                color: 'black',
                border: 'none',
                borderRadius: '50%',
                width: '36px',
                height: '36px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                boxShadow: '0 2px 10px rgba(0,0,0,0.3)',
                zIndex: 100
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </>
        )}
      </dialog>
    </div>
  );
};

export default Portfolio;
